/**
 * Nouvelle vidéo — format puis sujet (SPEC-UI §3.2).
 *
 * ⟦A-5 étage 6⟧ La vidéo **longue** n'existe pas au départ. Le moteur la refuse déjà
 * (`format_locked`, SPEC-MOTEUR §2.2) ; l'écran, lui, ne la montre pas du tout — l'avenant
 * demande une mécanique absente, pas un bouton cadenassé qui nargue.
 *
 * ⟦A-9⟧ **Le format se lit avant le texte.** Mathieu a joué une partie entière sans savoir
 * qu'il faisait des shorts : rien, nulle part, ne le disait. Les post-it prennent donc la
 * FORME de ce qu'on fabrique — **debout en 9:16 pour un short, couchés en 16:9 pour une
 * longue** — et portent leur ratio en tête de carte. On reconnaît ce qu'on va tourner à la
 * silhouette de l'écran, avant même d'avoir lu un titre.
 *
 * Le flux est **one-shot** (arbitrage A14) : cet écran ne dépense rien, il ouvre l'Atelier
 * côté client ; l'action `produce_video` unique part à la fin, avec tous les scores.
 *
 * ⟦A-11 §3⟧ **La production value se choisit ICI**, avant le premier mini-jeu — c'est tout le
 * sens de la mécanique : l'argent achète le *droit de garder* un meilleur geste, pas le geste.
 * Payer après coup aurait été acheter un score.
 *
 * ⟦C57 · lot 5d · A-174⟧ **Moins de texte.** Les titres de section tiennent en 5 mots, les chiffres
 * restent (`textes-courts.ts`, mesurés par un test) ; le principe de la production value, ce qu'est
 * l'équipe embauchée, l'offre qui a tourné et l'impasse du compte sont derrière « 📰 Les dessous »
 * de leur section, mot pour mot.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";

import {
  PALIER_PRODUCTION_VALUE,
  PRODUCTION_VALUE_PRIX_PLEIN,
  actionCost,
  bonusProductionValue,
  jalonsLisibles,
  mainAnnotee,
  plafondCompetence,
  type AtelierScores,
  type AtelierStage,
  type GameState,
  type VideoFormat,
} from "@maxime/shared";

import { vueMarche, type VueMarche } from "../../api/marche";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { indexBorne, indexCarteGauche, limitesDefilement, type LimitesDefilement } from "../../game/carrousel";
import { clientDeJeu } from "../../game/client";
import { contenu } from "../../game/content";
import { ouvert } from "../../game/escalier";
import { interrupteurs } from "../../game/menage";
import { ETAPES, badgeAlignement, libelleCout, nombre } from "../../game/format";
import { capacite } from "../../game/moteur";
import { useAtelierStore, etapesDuFormat } from "../../stores/atelierStore";
import { planPourFormat, useMarcheStore } from "../../stores/marcheStore";
import { useRunStore } from "../../stores/runStore";

import {
  EQUIPE_LONG,
  IMPASSE_COURT,
  IMPASSE_LONG,
  IMPASSE_TITRE,
  LIBELLE_EVERGREEN,
  PRODUCTION_VALUE_COURT,
  PRODUCTION_VALUE_LONG,
  TITRE_EQUIPE,
  TITRE_PRODUCTION_VALUE,
  TITRE_SUJET,
  ideesRequises,
  offrePerimeeCourt,
  offrePerimeeLong,
  plusDIdees,
  pleinTarifCourt,
  pleinTarifLong,
  raisonSansIdee,
} from "./textes-courts";

/** Scores neutres : sert uniquement à demander au moteur le coût d'un format (§2.2). */
const SCORES_VIDES: AtelierScores = {
  ecriture: 0,
  tournage: 0,
  montage: 0,
  miniature: null,
  promotion: null,
  delegated: [],
};

/** Le ratio de chaque format, tel qu'on le dit au joueur. */
const RATIO: Record<VideoFormat, string> = { short: "9:16", longue: "16:9" };

/**
 * 🔴 Le potentiel evergreen d'un sujet, **là où il se trouve selon le mode de jeu**.
 *
 * Le badge 🌲 SEO lisait `sujet.potentiel_evergreen` — celui du bundle de contenu. En **bac à
 * sable** il y est. En **mode compte** il n'y est jamais : `GET /api/content` retire le champ
 * *inconditionnellement* (`routes/content.ts`), et c'est volontaire — sans ce retrait, un
 * client lirait sans le nœud *SEO* ce que le nœud est censé révéler. Le serveur le repose
 * ailleurs, **sur la carte d'inspiration**, run par run, quand la capacité est acquise
 * (`routes/masquage.ts`, `InspirationPublique.potentiel_evergreen`).
 *
 * Les deux moitiés ne se rencontraient pas : le seul effet visible du nœud *SEO* sur cet écran
 * était **mort en mode compte** — acheté, actif dans le moteur, invisible à l'écran. On lit
 * donc la carte d'abord, le contenu ensuite. `InspirationCard` ne déclare pas le champ (le
 * moteur ne l'y écrit pas, c'est un ajout du masquage) : le cast dit exactement ça.
 */
function potentielEvergreen(
  carte: { subject_id: string },
  sujet: { potentiel_evergreen?: number } | null | undefined,
): number {
  const revele = (carte as { potentiel_evergreen?: number }).potentiel_evergreen;
  return revele ?? sujet?.potentiel_evergreen ?? 0;
}

/**
 * ⟦A-9 / A-11 §3⟧ Le plafond de compétence, **par branche**, avant et après la dépense.
 *
 * C'est l'affichage que l'avenant A-9 demande mot pour mot — « Écriture — 92 · plafonné à 45,
 * ton niveau » —, montré ici *avant* de jouer pour que la dépense soit un arbitrage éclairé.
 * Les deux valeurs viennent de `plafondCompetence` : rien n'est recalculé, la courbe en racine
 * et la borne à 100 sont celles du moteur.
 */
function PlafondsProduction({
  state,
  format,
  productionValue,
}: {
  state: GameState;
  format: VideoFormat;
  productionValue: number;
}) {
  const c = contenu();
  const etapes = etapesDuFormat(format);
  return (
    <ul className="recap">
      {etapes.map((stage: AtelierStage) => {
        const sans = plafondCompetence(state, stage, c);
        const avec = plafondCompetence(state, stage, c, { production_value: productionValue });
        return (
          <li key={stage}>
            <span>
              {ETAPES[stage].icone} {ETAPES[stage].nom}
            </span>
            <strong>
              {avec > sans ? `${sans} → ${avec}` : `${sans}`}
              {avec >= 100 ? " (max)" : ""}
            </strong>
          </li>
        );
      })}
    </ul>
  );
}

export function NouvelleVideo() {
  const run = useRunStore((s) => s.run);
  const demarrer = useAtelierStore((s) => s.demarrer);
  const plan = useMarcheStore((s) => s.plan);
  const viderPlan = useMarcheStore((s) => s.vider);
  const reprendreEtape = useMarcheStore((s) => s.reprendre);
  const naviguer = useNavigate();
  // ⟦A-4 E · M3.30⟧ L'offre de la fenêtre — pour afficher le DEVIS avant de lancer, et pour
  // repérer une embauche périmée (l'offre tourne tous les 7 jours simulés).
  const [marcheVue, setMarcheVue] = useState<VueMarche | null>(null);
  const clockPourMarche = run?.state.clock_h ?? 0;
  const planApplique = Object.keys(plan).length > 0;
  useEffect(() => {
    if (!run || !planApplique) return;
    let vivant = true;
    vueMarche(run)
      .then((v) => {
        if (vivant) setMarcheVue(v);
      })
      .catch(() => {
        /* pas de devis, pas de blocage : le moteur refusera proprement si besoin */
      });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.runId, clockPourMarche, planApplique]);
  const [format, setFormat] = useState<"short" | "longue">("short");
  const [sujetId, setSujetId] = useState<string | null>(null);
  const [evergreen, setEvergreen] = useState(false);
  const [productionValue, setProductionValue] = useState(0);
  // ⟦C29 · lot B · point 4a⟧ Le carrousel des idées : une seule rangée, on ne connaît la
  // position affichée que par le défilement réel (utile aussi au doigt, sans flèche).
  const railRef = useRef<HTMLDivElement>(null);
  interface EtatCarrousel extends LimitesDefilement {
    /** La carte visée par la dernière navigation (flèche/clavier), ou par le dernier défilement
     * réel (doigt/molette) — voir `carrousel.ts` pour pourquoi ces deux entrées ne calculent pas
     * cet index de la même façon. */
    index: number;
  }
  const ETAT_INITIAL: EtatCarrousel = { index: 0, debut: true, fin: false };
  const [carrousel, setCarrouselState] = useState<EtatCarrousel>(ETAT_INITIAL);
  // BUG RÉEL trouvé par la vérification contradictoire (tour 1, clics et touches rapprochés,
  // 100-250 ms) : les gestionnaires de clic/clavier lisaient l'état depuis la fermeture de leur
  // propre rendu, qui pouvait être en retard d'un commit React quand deux interactions
  // arrivaient plus vite que le rendu ne se recalait — la carte visée suivante se calculait
  // alors sur une position déjà dépassée (compteur bloqué ou revenant en arrière). Ce miroir
  // synchrone (mutable, jamais périmé) est ce que `deplacerCarrousel` lit à la place.
  const carrouselRef = useRef<EtatCarrousel>(ETAT_INITIAL);
  function fixerCarrousel(valeur: EtatCarrousel) {
    carrouselRef.current = valeur;
    setCarrouselState(valeur);
  }
  // BUG BLOQUANT trouvé par la vérification contradictoire (tour 3, clic direct sur une carte
  // hors de portée d'un pas de flèche) : `allerVersCarte` pose le bon index tout de suite, mais
  // déclenche aussi un vrai défilement ANIMÉ (`scrollTo({behavior:"smooth"})`) quand la carte
  // visée n'est pas déjà sous les yeux. Chaque frame de cette animation fait passer par
  // `onScroll` → `surDefilementRail`, qui recalculait l'index depuis la géométrie et écrasait le
  // compteur qu'on venait de poser — dans un rail large, plusieurs cartes de fin partagent la
  // même position de défilement maximale une fois écrêtée, et la dernière frame retombait
  // systématiquement sur la première d'entre elles, jamais sur celle réellement visée
  // (`verif-tour3/suspicion-clic-lointain-mesures.json` : clic sur la carte 5, compteur figé sur
  // « 3 / 6 »).
  //
  // Cette référence protège l'index tant qu'elle n'est pas nulle : `surDefilementRail` ignore
  // ALORS toute frame, sans essayer de deviner si l'animation est « arrivée » — un premier essai
  // qui réarmait le calcul géométrique dès que `scrollLeft` atteignait la position prédite s'est
  // fait avoir par un second `onScroll` identique juste après (règlement du `scroll-snap`, ou une
  // dernière frame d'animation répétée) : le premier confirmait l'arrivée et effaçait la
  // protection, le second la retrouvait déjà effacée et recalculait quand même la mauvaise carte
  // (mesuré : `apres-tour4/suspicion-clic-lointain-mesures.json`, essai intermédiaire). Seuls un
  // GESTE MANUEL (doigt, souris, molette — `surGesteManuelRail`) ou un NOUVEL appel à
  // `allerVersCarte` (qui remplace la cible protégée par la sienne) effacent cette protection :
  // jamais une simple position de défilement atteinte, aussi exacte soit-elle.
  const cibleProgrammeeRef = useRef<number | null>(null);
  function surGesteManuelRail() {
    cibleProgrammeeRef.current = null;
  }

  if (!run) return null;
  const state = run.state;
  const c = contenu();

  const longueOuverte = ouvert(state, "longue");
  const seoVisible = capacite(state, "potentiel_evergreen_visible");
  const evergreenPossible = capacite(state, "production_evergreen");

  // ⟦A-11 §3⟧ Ouverte à 100 000 abonnés — palier COURANT ou palier déjà franchi, comme le
  // moteur le lit (`bonusProductionValue`) : une chaîne qui redescend ne perd pas l'accès.
  // Dans les deux modes : l'API porte `production_value` depuis ⟦A-11⟧, et c'est le moteur qui
  // la débite et la refuse hors de ses bornes — jamais l'écran.
  //
  // ⟦C60 bis · A-177⟧ Matt (03/10/2026) : « les Clicks ne paient plus que le Marché et l'Équipe ». Quand le contenu
  // COUPE la production value (`systemes.production_value`), la carte n'existe plus à l'écran, et la dépense vaut
  // zéro : le moteur refuserait tout montant (`mecanique_verrouillee`). Rallumée, la carte d'avant revient.
  const productionValueOuverte =
    interrupteurs().production_value &&
    (state.subscribers >= PALIER_PRODUCTION_VALUE ||
      state.milestones_reached.includes(PALIER_PRODUCTION_VALUE));
  // On ne propose jamais plus que ce qu'il a : le moteur refuserait pour `not_enough_money`
  // après avoir joué cinq mini-jeux — la pire des façons d'apprendre une règle.
  const pvMax = productionValueOuverte
    ? Math.max(0, Math.min(PRODUCTION_VALUE_PRIX_PLEIN, Math.floor(state.money)))
    : 0;
  const pv = Math.min(productionValue, pvMax);

  // Les coûts se DEMANDENT au moteur, ils ne se recopient pas : la table de §2.2 a doublé
  // avec A-9 (short 3 h → 6 h), et l'écran affichait encore « 3 h · −12 ⚡ » en dur.
  const coutFormat = (f: VideoFormat) =>
    libelleCout(
      actionCost(
        f === "short"
          ? { type: "produce_video", format: "short", subject_id: "", scores: SCORES_VIDES }
          : {
              type: "produce_video",
              format: "longue",
              subject_id: "",
              evergreen_intent: false,
              extra_card_subjects: [],
              scores: SCORES_VIDES,
            },
        state,
      ),
    );

  /**
   * ⟦SPEC-UI §3.2⟧ **La troisième carte du croquis** — « JALON DISPO ? (si condition remplie) ».
   *
   * Le verdict vient du moteur (`jalonsLisibles`, le même sélecteur que l'écran des jalons) :
   * cet écran ne re-dérive aucune condition, il compte les `jouable`. Zéro jalon jouable ⇒ la
   * carte **n'existe pas** — la spec dit « visible uniquement si un jalon est disponible », et
   * ⟦A-5⟧ interdit le cadenas qui nargue. La carte ne lance rien ici : elle mène à l'écran des
   * jalons, où vivent déjà les conditions détaillées, le coût et le bouton « Produire » — on ne
   * duplique pas une chaîne qui existe, on y conduit.
   */
  // ⟦C60 · A-177⟧ Les jalons sont coupés : zéro jalon jouable, la carte n'existe pas (et le chemin qu'elle
  // ouvrait, l'écran des jalons, est fermé). Rallumés, le sélecteur du moteur reprend la main.
  const jalonsJouables = interrupteurs().jalons ? jalonsLisibles(state, c).filter((l) => l.jouable) : [];
  // ⟦C62 · lot atelier · friction 3⟧ Le choix du format ne s'affiche que s'il y a un choix (ou un jalon à annoncer).
  const formatChoisissable = longueOuverte || jalonsJouables.length > 0 || format === "longue";
  // Sans section « 1. Le format », plus de numéros : « Le sujet », « Production value ».
  const sansNumero = (titre: string) => (formatChoisissable ? titre : titre.replace(/^\d+\. /, ""));

  const sujet = sujetId ? c.parSujet.get(sujetId) : null;
  // ⟦C62 · lot événements · A-179⟧ Aucune idée en main : le bouton est grisé et DIT pourquoi. La durée de la veille
  // vient du moteur, la même que celle du bouton « Veille » (le texte écrivait 2 h quand le bouton en annonçait 4).
  const sansIdee = state.inspiration.length === 0;
  const veilleOuverte = ouvert(state, "veille");
  const heuresVeille = actionCost({ type: "veille" }, state).hours;
  // La longue consomme 2 cartes (3 en evergreen) : le sujet principal + des cartes en plus.
  const cartesRequises = format === "short" ? 1 : evergreen ? 3 : 2;
  const extras = state.inspiration
    .map((carte) => carte.subject_id)
    .filter((id) => id !== sujetId)
    .slice(0, cartesRequises - 1);
  const assezDeCartes = sujetId !== null && extras.length >= cartesRequises - 1;
  /** Ce que le client de jeu actif sait faire — pas une règle, une capacité de transport. */
  const productionOuverte = clientDeJeu().capacites.production;

  // ⟦A-4 E · M3.30⟧ Le plan d'embauche du marché, restreint aux étapes de CE format : un
  // short n'a ni miniature ni promotion — on n'emporte pas une embauche qu'on ne peut pas
  // facturer. Le plan est CONSOMMÉ au lancement : la prochaine production repart de zéro.
  const planMarche = planPourFormat(plan, format);
  /** L'offre correspondante de la fenêtre COURANTE — `undefined` = l'offre a tourné. */
  const offreDe = (stage: AtelierStage) =>
    marcheVue?.etapes[stage]?.find((o) => o.id === planMarche[stage]);
  const etapesEmbauchees = etapesDuFormat(format).filter((s) => planMarche[s] !== undefined);
  const etapesPerimees = marcheVue === null ? [] : etapesEmbauchees.filter((s) => offreDe(s) === undefined);
  // Le plan qui PART : sans les périmées — le moteur refuserait `indisponible` après les
  // mini-jeux, la pire façon d'apprendre que l'offre tourne.
  const planValide: typeof planMarche = { ...planMarche };
  for (const s of etapesPerimees) delete planValide[s];
  const etapesValides = etapesEmbauchees.filter((s) => !etapesPerimees.includes(s));
  const devisTotal = etapesValides.reduce((t, s) => t + (offreDe(s)?.prix ?? 0), 0);

  function lancer() {
    if (!sujetId || !assezDeCartes || !productionOuverte) return;
    demarrer({
      format,
      subjectId: sujetId,
      evergreenIntent: format === "longue" && evergreen,
      extraCardSubjects: format === "longue" ? extras : [],
      productionValue: productionValueOuverte ? pv : 0,
      ...(etapesValides.length > 0 ? { embauches: planValide } : {}),
    });
    viderPlan();
    naviguer("/atelier");
  }

  // ⟦C29 · lot B · point 4a⟧ Le carrousel des idées : une seule rangée, défilement horizontal.
  const main = mainAnnotee(state);

  /**
   * Fait défiler le rail jusqu'à la carte visée (bornée), et pose IMMÉDIATEMENT le compteur et
   * le focus sur CETTE carte — sans attendre l'événement `scroll`, et sans jamais recalculer une
   * autre carte « réelle » à sa place (voir l'en-tête de `carrousel.ts` : c'est précisément cette
   * seconde idée qui créait le point fixe du tour 2). Seules ◀/▶ restent sur la géométrie RÉELLE
   * — `limitesDefilement`, sur la position honnêtement PRÉDITE (`scrollTo` écrête à `scrollMax` :
   * la position où le défilement va vraiment s'arrêter est `min(offset visé, scrollMax)`, jamais
   * l'offset brut) — car un rail large peut être déjà tout à fait au bout avant que l'index visé
   * n'atteigne la dernière carte.
   *
   * ⟦tour 3⟧ BUG BLOQUANT (six idées, le cas normal) : reculer d'une carte quand le rail était
   * déjà à son maximum ne faisait plus RIEN, ni au compteur ni au focus, ni à ◀ elle-même — la
   * carte visée ne bougeait pas d'un pixel (déjà écrêtée là), et un rail qui ne bouge pas ne
   * déclenche aucun événement `scroll` pour corriger l'affichage ensuite. Poser l'index sur la
   * carte VISÉE (`borne`, un simple compteur `± 1` sans aucune géométrie) plutôt que de le
   * redéduire de la position de défilement supprime le point fixe à la racine : reculer d'une
   * carte change TOUJOURS `borne`, que le rail bouge ou non.
   */
  function allerVersCarte(cible: number, options?: { focus?: boolean }) {
    const rail = railRef.current;
    if (!rail) return;
    const cartes = Array.from(rail.children) as HTMLElement[];
    const borne = indexBorne(cartes.length, cible);
    const carteVisee = cartes[borne];
    if (!carteVisee) return;
    const scrollMax = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const scrollPredit = Math.min(carteVisee.offsetLeft, scrollMax);
    // ⟦tour 4⟧ Posée AVANT `scrollTo` : la toute première frame de l'animation (parfois
    // synchrone) doit déjà trouver la cible en place pour être ignorée par `surDefilementRail`.
    cibleProgrammeeRef.current = scrollPredit;
    rail.scrollTo({ left: carteVisee.offsetLeft, behavior: "smooth" });
    fixerCarrousel({ index: borne, ...limitesDefilement(scrollPredit, scrollMax) });
    // BUG RÉEL (tour 1, clavier à cadence normale) : sans `preventScroll`, le focus posé sur la
    // carte déclenche SON PROPRE ajustement de défilement natif du navigateur, qui interrompt le
    // `scrollTo` ci-dessus en plein vol.
    if (options?.focus) carteVisee.focus({ preventScroll: true });
  }

  /**
   * Avance ou recule d'une carte (flèches ◀ ▶, clavier).
   *
   * BUG RÉEL (tour 1, clics à 100 ms et flèche clavier à 250 ms) : cette fonction visait
   * `index ± 1` lu depuis la fermeture du gestionnaire d'événement — capturé au dernier rendu,
   * donc potentiellement EN RETARD d'un commit React quand deux interactions arrivaient plus
   * vite que le rendu ne se recalait. La cible suivante se calculait alors sur une position déjà
   * dépassée : le compteur restait bloqué (5 clics rapides → toujours « 4 / 6 », la flèche ▶
   * restant pourtant active). `carrouselRef` est un miroir synchrone, jamais périmé.
   */
  function deplacerCarrousel(delta: number, options?: { focus?: boolean }) {
    allerVersCarte(carrouselRef.current.index + delta, options);
  }

  /**
   * Recale le compteur et les flèches sur ce que le défilement affiche vraiment — le seul chemin
   * qui n'a PAS de « carte visée » explicite (glisser au doigt, molette) : l'index vient donc de
   * la géométrie réelle (`indexCarteGauche`), pas d'un compteur `± 1`.
   *
   * ⟦tour 4⟧ SAUF pendant un défilement PROGRAMMÉ encore protégé (`allerVersCarte`, déclenché par
   * un clic sur une carte ou par une flèche/le clavier) : chaque frame de son animation `smooth`
   * passe aussi par ici, et recalculer la géométrie à CE moment-là écraserait l'index déjà posé
   * par `allerVersCarte` avec la carte géométriquement au bord — pas celle réellement visée (voir
   * l'en-tête de `carrousel.ts`). Tant que `cibleProgrammeeRef` n'est pas nul on ignore TOUTE
   * frame, sans essayer de deviner si l'animation est arrivée à destination (voir le commentaire
   * de `cibleProgrammeeRef` : cette détection-là s'est révélée fautive).
   */
  function surDefilementRail() {
    const rail = railRef.current;
    if (!rail) return;
    if (cibleProgrammeeRef.current !== null) return;
    const offsets = (Array.from(rail.children) as HTMLElement[]).map((carte) => carte.offsetLeft);
    const scrollMax = Math.max(0, rail.scrollWidth - rail.clientWidth);
    fixerCarrousel({
      index: indexCarteGauche(offsets, rail.scrollLeft),
      ...limitesDefilement(rail.scrollLeft, scrollMax),
    });
  }

  function surClavierRail(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      deplacerCarrousel(-1, { focus: true });
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      deplacerCarrousel(1, { focus: true });
    }
  }

  // Établit le compteur et l'état ◀/▶ réels dès le premier rendu du rail (nombre d'idées ou
  // format changés) : sans ceci, l'état de départ restait la valeur par défaut posée au montage
  // (`fin: false`), fausse dès qu'une seule ou deux idées tiennent déjà tout le cadre — ▶
  // resterait activée pour ne rien faire au premier clic.
  useEffect(() => {
    // Le jeu de cartes change entièrement (nombre ou format) : un défilement programmé encore
    // référencé ici viserait un rail qui n'existe plus dans cet état.
    cibleProgrammeeRef.current = null;
    surDefilementRail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [main.length, format]);

  // La carte choisie reste toujours visible dans le rail, même si la sélection change
  // autrement qu'en cliquant dessus (elle est alors forcément déjà visible).
  useEffect(() => {
    if (!sujetId) return;
    const position = main.findIndex((m) => m.carte.subject_id === sujetId);
    if (position >= 0) allerVersCarte(position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sujetId]);

  // ⟦C29 · lot B · points 4b/4c⟧ Le SEUL bouton Lancer de l'écran (le doublon d'en-tête ⟦C16⟧
  // est retiré : il compensait un défilement que le carrousel du point 4a empêche déjà
  // structurellement, quel que soit le nombre d'idées en main). ⟦C44 · A-166⟧ **Posé dans le
  // PIED de la fenêtre**, hors du corps qui défile (`Panneau.piedDePage` → `Fenetre.pied`) :
  // retour de Matt du 18/09/2026, « je suis obligé de scroller juste pour cliquer sur le
  // bouton Lancer » — désormais visible sans défiler, à 1280×800 comme à 1920×1080 (mesures
  // dans `docs/chantiers/C44-tout-en-fenetres.md`). Un vrai bouton d'action — `.lancer-cta`,
  // pas `.bouton.grand` — pour ne pas prendre la carrure plate pleine largeur des mini-jeux :
  // sur ordinateur il reprend une largeur de bouton, pas de bandeau ; sous 960 px (panneau
  // plein écran, voir `.panneau` dans `app.css`) il redevient pleine largeur, à l'aise au pouce.
  const piedLancer = (
    <div className="barre-actions">
      <button
        className="bouton primaire lancer-cta"
        type="button"
        disabled={!sujetId || !assezDeCartes || !productionOuverte}
        onClick={lancer}
      >
        ▶ Lancer la production
        <span className="cout">
          {sansIdee
            ? raisonSansIdee(veilleOuverte)
            : sujet
              ? `« ${sujet.nom} » · ${etapesDuFormat(format).length} étapes`
              : "choisis un sujet"}
          {productionValueOuverte && pv > 0 ? ` · ${nombre(pv)} ₵ de production value` : ""}
        </span>
      </button>
      {sujetId && !assezDeCartes ? (
        <p className="rappel-escalier">{ideesRequises(cartesRequises)}</p>
      ) : null}
    </div>
  );

  return (
    <Panneau
      // ⟦C62 · A-179⟧ « 2 idées », pas le pictogramme de la carte : un seul mot pour la même chose, partout.
      titre={`🎬 Nouvelle vidéo · ⚡ ${Math.round(state.energy)} · ${state.inspiration.length} idée${state.inspiration.length > 1 ? "s" : ""}`}
      piedDePage={piedLancer}
    >
      {/* ⟦C62 · lot atelier · friction 3⟧ **Le choix du format n'existe que s'il y a un choix.** Au départ, il n'y a
          qu'une option (« Short ») : une section « 1. Le format » avec un seul onglet n'est qu'un clic imaginaire,
          et un titre numéroté qui en annonce deux autres. Elle apparaît dès que la longue s'ouvre, ou qu'un jalon
          est jouable, ou qu'on est déjà en longue : alors elle est là, comme avant. */}
      {formatChoisissable ? (
        <section className="carte">
          <h2 className="bloc-titre">1. Le format</h2>
          {/* ⟦C44 · A-166⟧ Le format devient une rangée d'onglets, façon classeur à l'ancienne —
              retour de Matt du 18/09/2026 : « le premier truc qui permet de switcher entre short
              et vidéo longue n'a pas besoin d'être aussi gros, ça peut être juste un onglet, en
              mode fichier un peu ». Remplace les deux grandes cartes de `.choix-modes` (radios),
              qui mangeaient la moitié de la hauteur — c'est ce qui obligeait à défiler pour
              atteindre « Lancer ». Deux boutons simples (pas le patron ARIA `tablist`/`tab`, qui
              imposerait une navigation aux flèches pour deux onglets seulement — Tab suffit ici,
              comme pour tout le reste de l'écran), le coût et la durée du format actif sur UNE
              ligne sous les onglets. */}
          <div className="onglets-format">
            <button
              type="button"
              aria-pressed={format === "short"}
              className={`onglet-format ${format === "short" ? "actif" : ""}`}
              onClick={() => {
                setFormat("short");
                setEvergreen(false);
              }}
            >
              {/* ⟦A-9⟧ La silhouette d'abord, le mot ensuite : c'est elle qu'on reconnaît. */}
              <span className="format-ecran format-ecran-short" aria-hidden="true" />
              Short <span className="format-ratio">{RATIO.short}</span>
            </button>

            {longueOuverte ? (
              <button
                type="button"
                aria-pressed={format === "longue"}
                className={`onglet-format ${format === "longue" ? "actif" : ""}`}
                onClick={() => setFormat("longue")}
              >
                <span className="format-ecran format-ecran-longue" aria-hidden="true" />
                Longue <span className="format-ratio">{RATIO.longue}</span>
              </button>
            ) : null}
          </div>
          <p className="format-ligne-cout">
            {coutFormat(format)} · {etapesDuFormat(format).length} mini-jeux ·{" "}
            {format === "short" ? "vertical" : "horizontal"}
          </p>

          {format === "longue" && evergreenPossible ? (
            <label className="case">
              <input
                type="checkbox"
                checked={evergreen}
                onChange={(e) => setEvergreen(e.target.checked)}
              />
              {LIBELLE_EVERGREEN}
            </label>
          ) : null}

          {jalonsJouables.length > 0 ? (
            <button
              type="button"
              className="mode mode-jalon mode-jalon-bandeau"
              onClick={() => naviguer("/jalons")}
            >
              <span className="format-ecran format-ecran-longue" aria-hidden="true" />
              <strong>
                🏆 Jalon disponible <span className="format-ratio">{RATIO.longue}</span>
              </strong>
              <span>
                {jalonsJouables.length === 1
                  ? `« ${jalonsJouables[0]!.nom} » est prête à se tourner`
                  : `${jalonsJouables.length} vidéos jalons sont prêtes à se tourner`}
              </span>
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="carte">
        <h2 className="bloc-titre">
          {sansNumero(TITRE_SUJET)} <span className="compteur">{RATIO[format]}</span>
        </h2>

        {state.inspiration.length === 0 ? (
          <p className="vide">{plusDIdees(veilleOuverte, heuresVeille)}</p>
        ) : (
          // ⟦C29 · lot B · point 4a⟧ Une seule rangée, défilement horizontal avec accroche
          // (`scroll-snap`) : la grille d'⟦A-9⟧ empilait les post-it sur plusieurs rangées dès
          // qu'il y en avait plus d'une poignée (6 au plafond, `shared/engine/idees.ts`),
          // poussant tout ce qui suit hors de l'écran (point 4d). Le ratio par format et le
          // choc visuel du changement de forme restent identiques : seule la mise en rang change.
          <div className="carrousel-idees">
            <div className="carrousel-rangee">
              <button
                type="button"
                className="bouton discret carrousel-fleche"
                onClick={() => deplacerCarrousel(-1)}
                disabled={carrousel.debut}
                aria-label="Idée précédente"
              >
                ◀
              </button>
              <div
                className={`inspiration inspiration-${format}`}
                ref={railRef}
                role="group"
                aria-roledescription="carrousel"
                aria-label={`Tes idées en main, au format ${RATIO[format]}`}
                onScroll={surDefilementRail}
                onKeyDown={surClavierRail}
                onPointerDown={surGesteManuelRail}
                onTouchStart={surGesteManuelRail}
                onWheel={surGesteManuelRail}
              >
                {/* ⟦A-15 §1⟧ `mainAnnotee` rend la main AVEC sa fraîcheur — c'est le télégraphe de
                    l'avenant : « une idée qui expire se voit venir ». Le moteur écrivait la lecture
                    depuis le début, l'écran ne l'affichait nulle part, si bien que la carte
                    disparaissait sans préavis et que le joueur ne l'apprenait qu'au récapitulatif.
                    Une carte sans échéance (`eternelle`) ne porte aucun badge : la main de départ
                    reste exactement ce qu'elle était. */}
                {main.map(({ carte, fraicheur }) => {
                  const s = c.parSujet.get(carte.subject_id);
                  const badge = badgeAlignement(s?.alignement ?? 0);
                  const chaud =
                    ouvert(state, "tendances") &&
                    state.trends.some((t) => t.trend_id !== "" && s && t.themes.includes(s.theme));
                  return (
                    <button
                      type="button"
                      key={carte.subject_id}
                      className={`postit choisissable ${sujetId === carte.subject_id ? "choisi" : ""}`}
                      onClick={() => setSujetId(carte.subject_id)}
                      aria-label={`${s?.nom ?? carte.subject_id} — ${
                        format === "short" ? "short vertical 9:16" : "vidéo longue horizontale 16:9"
                      }`}
                    >
                      {/* Le ratio est le PREMIER élément de la carte : il se lit avant le titre. */}
                      <span className="postit-format" aria-hidden="true">
                        <i className={`format-ecran format-ecran-${format}`} />
                        {RATIO[format]}
                      </span>
                      <span className="postit-nom">{s?.nom ?? carte.subject_id}</span>
                      <span className="postit-badges">
                        {ouvert(state, "image") ? (
                          <span>
                            {badge.icone} {badge.libelle}
                          </span>
                        ) : null}
                        {chaud ? <span className="chaud">🔥 chaud</span> : null}
                        {seoVisible && potentielEvergreen(carte, s) >= 60 ? <span>🌲 SEO</span> : null}
                        {fraicheur.bande === "bientot_perimee" ? (
                          <span className="tiedit" title="Plus personne n'en parlera bientôt : joue-la ou repars en veille.">
                            ⏳ dernier jour
                          </span>
                        ) : fraicheur.bande === "tiede" ? (
                          <span
                            className="tiedit"
                            title="Cette idée refroidit : elle quittera ta main si tu la laisses traîner."
                          >
                            ⏳ {nombre(fraicheur.jours_restants ?? 0)} j
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="bouton discret carrousel-fleche"
                onClick={() => deplacerCarrousel(1)}
                disabled={carrousel.fin}
                aria-label="Idée suivante"
              >
                ▶
              </button>
            </div>
            {/* Compte la carte VISÉE par la dernière flèche/touche (ou, au doigt, la carte la
                plus à gauche déjà rentrée dans le cadre) — pas une « distance au bout » : dans un
                rail large où plusieurs cartes tiennent déjà côte à côte, ▶ peut se désactiver
                avant que ce nombre n'atteigne le total (`carrousel.ts`, `limitesDefilement`). */}
            <p className="carrousel-compteur" aria-live="polite">
              {carrousel.index + 1} / {main.length}
            </p>
          </div>
        )}
      </section>

      {/* ⟦A-11 §3⟧ « L'argent achète du POTENTIEL, jamais du score. » L'écran doit le DIRE :
          sans ça, un curseur avec un prix se lit comme un achat de résultat. */}
      {productionValueOuverte ? (
        <section className="carte">
          <h2 className="bloc-titre">
            {sansNumero(TITRE_PRODUCTION_VALUE)} <span className="compteur">{nombre(state.money)} ₵</span>
          </h2>
          <p className="ligne-info">
            {/* ⟦A-47⟧ « ce que ton geste peut valoir » est devenu faux le jour où le plafond a
                cessé de couper : au-dessus de lui, un geste vaut encore plus que lui. Ce que la
                dépense déplace, et la seule chose qu'elle déplace, c'est le seuil jusqu'où la
                chaîne exploite **pleinement** — ce que ces trois chiffres montrent.
                ⟦C57 · lot 5d⟧ Le principe en une phrase ; la phrase entière et le plein tarif : dessous. */}
            {PRODUCTION_VALUE_COURT}{" "}
            <LesDessous titre="La production value">
              <DessousBloc>
                <p className="dessous-texte">{PRODUCTION_VALUE_LONG}</p>
                <p className="dessous-texte">
                  Tes Clicks : {nombre(state.money)} ₵. {pleinTarifLong({ plein: PRODUCTION_VALUE_PRIX_PLEIN, max: pvMax })}
                </p>
              </DessousBloc>
            </LesDessous>
          </p>
          <label className="champ">
            <span className="champ-label">
              {pv === 0 ? "Aucune dépense" : `${nombre(pv)} ₵ investis`} — plafond{" "}
              {pv === 0 ? "inchangé" : `+${bonusProductionValue(state, pv).toFixed(1).replace(".", ",")}`}
            </span>
            <input
              className="production-curseur"
              type="range"
              min={0}
              max={PRODUCTION_VALUE_PRIX_PLEIN}
              step={25}
              value={pv}
              onChange={(e) => setProductionValue(Math.min(pvMax, Number(e.target.value)))}
            />
          </label>
          <PlafondsProduction state={state} format={format} productionValue={pv} />
          <p className="ligne-info discret">
            {pleinTarifCourt({ plein: PRODUCTION_VALUE_PRIX_PLEIN, max: pvMax })}
          </p>
        </section>
      ) : null}

      {/* Une impasse de transport se dit AVANT les cinq mini-jeux, jamais après. Il n'y en a
          plus aujourd'hui pour la production ; le garde-fou reste, parce qu'une capacité peut
          se refermer (un serveur démarré sans `content/atelier.json` refuse tout Atelier). */}
      {productionOuverte ? null : (
        <section className="carte carte-avertissement">
          <h2 className="bloc-titre">{IMPASSE_TITRE}</h2>
          <p className="ligne-info">
            {IMPASSE_COURT}{" "}
            <LesDessous titre="Pourquoi pas de compte">
              <DessousBloc>
                {IMPASSE_LONG.map((paragraphe) => (
                  <p className="dessous-texte" key={paragraphe}>
                    {paragraphe}
                  </p>
                ))}
              </DessousBloc>
            </LesDessous>
          </p>
        </section>
      )}

      {/* ⟦A-4 E · M3.30⟧ L'équipe embauchée au marché — le DEVIS avant de lancer, la donnée
          que la délégation transitoire prélevait en silence. ⟦A-45⟧ : le prix se voit AVANT. */}
      {etapesEmbauchees.length > 0 ? (
        <section className="carte">
          <h2 className="bloc-titre">{TITRE_EQUIPE}</h2>
          <ul className="recap">
            {etapesValides.map((stage) => {
              const offre = offreDe(stage);
              return (
                <li key={stage}>
                  <span>
                    {ETAPES[stage].icone} {ETAPES[stage].nom} —{" "}
                    {offre?.nom ?? c.parFreelance.get(planMarche[stage] ?? "")?.nom ?? planMarche[stage]}
                  </span>
                  <strong>{offre === undefined ? "…" : `${nombre(offre.prix)} ₵`}</strong>
                </li>
              );
            })}
            {etapesValides.length > 0 ? (
              <li>
                <span>
                  <strong>Devis</strong> — prélevé à la publication
                </span>
                <strong>{marcheVue === null ? "…" : `${nombre(devisTotal)} ₵`}</strong>
              </li>
            ) : null}
          </ul>
          {etapesPerimees.length > 0 ? (
            <p className="ligne-info">
              {offrePerimeeCourt(etapesPerimees.map((stage) => ETAPES[stage].nom.toLowerCase()))}
            </p>
          ) : null}
          <div className="actions">
            <button className="bouton discret" type="button" onClick={() => naviguer("/marche")}>
              🧑‍🔧 Revoir le marché
            </button>
            {etapesValides.map((stage) => (
              <button
                key={stage}
                className="bouton discret"
                type="button"
                onClick={() => reprendreEtape(stage)}
              >
                Reprendre {ETAPES[stage].nom.toLowerCase()}
              </button>
            ))}
            {/* ⟦C57 · lot 5d⟧ Ce que veut dire « embauchée », et l'offre qui a tourné, en toutes lettres. */}
            <LesDessous titre={TITRE_EQUIPE}>
              <DessousBloc>
                <p className="dessous-texte">{EQUIPE_LONG}</p>
                {etapesPerimees.length > 0 ? (
                  <p className="dessous-texte">
                    {offrePerimeeLong(etapesPerimees.map((stage) => ETAPES[stage].nom.toLowerCase()))}
                  </p>
                ) : null}
              </DessousBloc>
            </LesDessous>
          </div>
        </section>
      ) : null}
    </Panneau>
  );
}
